import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Paper,
  Typography,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Grid,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  createProduct, 
  updateProduct, 
  getProduct, 
  getSKUs, 
  updateSKU, 
  deleteProduct,
  getProductAttributes,
  createProductAttribute,
  updateProductAttribute,
  getAffectedSKUsByAttribute,
  deleteProductAttribute,
  createProductAttributeOption,
  getAffectedSKUsByOption,
  deleteProductAttributeOption
} from '../app/api';
import { useTranslation } from '../app/LanguageContext';
import ConfirmDialog from '../components/ConfirmDialog';
import MediaGallery from '../components/MediaGallery';
import FieldHelpIcon from '../components/FieldHelpIcon';
import { applyCurrencyMask, parseCurrency, formatCurrency } from '../app/currency';

const customizableTypeLabels = { text: 'Texto', number: 'Número', date: 'Data', textarea: 'Texto longo', select: 'Seleção' };

const SortableAttributeItem = ({
  attribute,
  onDelete,
  onAddOption,
  editingOptionAttributeId,
  setEditingOptionAttributeId,
  handleDeleteOption,
  onDragEndOptions,
  optionSensors,
  isEditing,
  onEdit,
  onSaveEdit,
  onCancelEdit,
  onAttributeChange,
}) => {
  const { translate } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: attribute.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Paper ref={setNodeRef} style={style} sx={{ p: 2, mb: 2, cursor: 'grab', '&:active': { cursor: 'grabbing' } }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={1}>
          <IconButton
            {...attributes}
            {...listeners}
            size="small"
            sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
          >
            <DragIndicatorIcon />
          </IconButton>
        </Grid>
        <Grid item xs={12} md={2}>
          <Typography variant="subtitle1" fontWeight="bold">
            {attribute.name || attribute.key}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {attribute.is_required && <Chip label={translate('products.attribute_required')} size="small" color="primary" />}
            {attribute.is_variation && <Chip label={translate('products.attribute_variation')} size="small" />}
            {attribute.is_customizable && (
              <Chip
                label={`${translate('products.attribute_customizable')}${attribute.customizable_type ? ` (${customizableTypeLabels[attribute.customizable_type] || attribute.customizable_type})` : ''}`}
                size="small"
              />
            )}
            {attribute.is_customizable && attribute.customizable_type === 'select' && !isEditing && (
              <Button size="small" variant="outlined" onClick={onEdit}>
                Editar
              </Button>
            )}
            {isEditing && attribute.customizable_type === 'select' && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={attribute.allow_other || false}
                      onChange={(e) => onAttributeChange(attribute.id, 'allow_other', e.target.checked)}
                    />
                  }
                  label={translate('products.attribute_allow_other')}
                />
                <Button size="small" variant="contained" onClick={() => onSaveEdit(attribute.id)}>
                  Salvar
                </Button>
                <Button size="small" onClick={onCancelEdit}>
                  Cancelar
                </Button>
              </Box>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            {translate('products.attribute_options')}:
            <FieldHelpIcon title={translate('products.attribute_options_help')} />
          </Typography>
          {attribute.is_customizable && attribute.customizable_type !== 'select' ? (
            <Typography variant="body2" color="text.secondary">
              Preenchido pelo cliente no orçamento
            </Typography>
          ) : (attribute.is_variation || (attribute.is_customizable && attribute.customizable_type === 'select')) && attribute.options && attribute.options.length > 0 ? (
            <DndContext
              sensors={optionSensors}
              collisionDetection={closestCenter}
              onDragEnd={(event) => onDragEndOptions(event, attribute.id)}
            >
              <SortableContext
                items={attribute.options.map(opt => opt.id)}
                strategy={verticalListSortingStrategy}
              >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  {attribute.options.map((opt) => (
                    <SortableOptionItem
                      key={opt.id}
                      option={opt}
                      onDelete={() => handleDeleteOption(attribute.id, opt.id)}
                    />
                  ))}
                </Box>
              </SortableContext>
            </DndContext>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Nenhuma opção definida
            </Typography>
          )}
          {((attribute.is_variation || (attribute.is_customizable && attribute.customizable_type === 'select')) && editingOptionAttributeId === attribute.id) ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Nova opção"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    onAddOption(attribute.id, e.target.value);
                    e.target.value = '';
                  }
                }}
                autoFocus
              />
              <Button
                size="small"
                onClick={() => setEditingOptionAttributeId(null)}
              >
                Cancelar
              </Button>
            </Box>
          ) : (attribute.is_variation || (attribute.is_customizable && attribute.customizable_type === 'select')) ? (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setEditingOptionAttributeId(attribute.id)}
            >
              Adicionar Opção
            </Button>
          ) : null}
        </Grid>
        <Grid item xs={12} md={3}>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => onDelete(attribute.id)}
            >
              Excluir
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

// Sortable Option Item Component
const SortableOptionItem = ({ option, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Chip
        label={option.value}
        size="small"
        icon={<DragIndicatorIcon sx={{ fontSize: 16 }} />}
        sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
        {...attributes}
        {...listeners}
      />
      <IconButton
        size="small"
        onClick={onDelete}
        aria-label={`Excluir opção ${option.value}`}
        sx={{ p: 0.25 }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

const ProductFormPage = () => {
  const { translate } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    status: 'ACTIVE',
    type: 'PRODUCT',
  });
  const [skus, setSkus] = useState([]);
  const [savingSkuIds, setSavingSkuIds] = useState(new Set());
  const [errors, setErrors] = useState({});
  const [skuErrors, setSkuErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, payload: null });
  const [productId, setProductId] = useState(null);
  const [productAttributes, setProductAttributes] = useState([]);
  const [editingAttributeId, setEditingAttributeId] = useState(null);
  const [newAttribute, setNewAttribute] = useState(null);
  const [editingOptionAttributeId, setEditingOptionAttributeId] = useState(null);

  useEffect(() => {
    if (isEdit) {
      loadData();
    } else {
      setLoadingData(false);
    }
  }, [id]);

  const loadData = async (showLoading = true) => {
    try {
      if (showLoading) setLoadingData(true);
      const [productResponse, skusResponse, attributesResponse] = await Promise.all([
        getProduct(id),
        getSKUs(id),
        getProductAttributes(id).catch(() => []),
      ]);
      if (productResponse.data) {
        setFormData({
          code: productResponse.data.code || '',
          name: productResponse.data.name,
          description: productResponse.data.description,
          status: productResponse.data.status,
          type: productResponse.data.type || 'PRODUCT',
        });
        setProductId(productResponse.data.id);
      }
      setSkus(skusResponse.data || []);
      // Handle attributes response - it can be an array directly or wrapped in data
      const attributes = Array.isArray(attributesResponse) ? attributesResponse : (attributesResponse?.data || []);
      // Sort attributes by display_order
      const sortedAttributes = [...attributes].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      // Sort options within each attribute
      sortedAttributes.forEach(attr => {
        if (attr.options && attr.options.length > 0) {
          attr.options.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
        }
      });
      setProductAttributes(sortedAttributes);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = (dataToValidate = formData) => {
    const newErrors = {};

    if (!dataToValidate.name || dataToValidate.name.trim() === '') {
      newErrors.name = translate('products.name_required');
    }

    if (!dataToValidate.status) {
      newErrors.status = translate('products.status_required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate(formData)) {
      return;
    }

    setLoading(true);

    try {
      const productData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        status: formData.status,
        type: formData.type || 'PRODUCT',
      };

      if (isEdit) {
        await updateProduct(id, productData);
        setSnackbar({
          open: true,
          message: translate('products.updated'),
          severity: 'success',
        });
      } else {
        const response = await createProduct(productData);
        setSnackbar({
          open: true,
          message: translate('products.created'),
          severity: 'success',
        });
        setTimeout(() => {
          navigate(`/products/${response.data.code || response.data.id}/edit`);
        }, 1000);
        return;
      }

      setTimeout(() => {
        navigate(`/products/${formData.code || id}`);
      }, 1000);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const formatAttributes = (attributes) => {
    if (!attributes) return '-';
    const entries = typeof attributes === 'object' && !Array.isArray(attributes)
      ? Object.entries(attributes)
      : Array.isArray(attributes)
        ? attributes.map((a) => [a.key || a.attribute_key, a.value || a.attribute_value])
        : [];
    if (entries.length === 0) return '-';
    return entries
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
  };

  const handleSkuChange = (skuId, field, value, options = {}) => {
    let processedValue = value;
    if (field === 'price') {
      const cleaned = value.toString().replace(/\D/g, '');
      processedValue = cleaned === '' ? '' : applyCurrencyMask(cleaned);
    } else if (field === 'manufacturing_days') {
      const n = parseInt(value, 10);
      processedValue = value === '' ? '' : (isNaN(n) ? 0 : n);
    }
    setSkus((prev) =>
      prev.map((sku) => {
        if (sku.id !== skuId) return sku;
        const next = { ...sku, [field]: processedValue };
        if (field === 'is_made_to_order' && !processedValue) {
          next.manufacturing_days = 0;
        }
        return next;
      })
    );
    if (skuErrors[`${skuId}_${field}`]) {
      setSkuErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`${skuId}_${field}`];
        return newErrors;
      });
    }
    if (options.autoSave) {
      const override = field === 'status'
        ? { status: value }
        : field === 'is_made_to_order'
          ? { is_made_to_order: !!value, manufacturing_days: value ? 0 : undefined }
          : {};
      handleSaveSku(skuId, override);
    }
  };

  const validateSku = (skuData, skuId) => {
    const newErrors = {};
    const isMTO = !!skuData.is_made_to_order;

    const priceVal = typeof skuData.price === 'string' ? parseFloat(parseCurrency(skuData.price)) : (skuData.price ?? 0);
    if (isNaN(priceVal) || priceVal < 0) {
      newErrors[`${skuId}_price`] = 'Preço inválido';
    }

    if (isMTO) {
      const days = parseInt(skuData.manufacturing_days, 10);
      if (skuData.manufacturing_days === '' || skuData.manufacturing_days === null || skuData.manufacturing_days === undefined) {
        newErrors[`${skuId}_manufacturing_days`] = translate('products.manufacturing_days_required');
      } else if (isNaN(days) || days < 0) {
        newErrors[`${skuId}_manufacturing_days`] = translate('products.manufacturing_days_invalid');
      }
    } else {
      if (skuData.stock === '' || skuData.stock === null || skuData.stock === undefined) {
        newErrors[`${skuId}_stock`] = 'Estoque é obrigatório';
      } else {
        const stockValue = parseInt(skuData.stock, 10);
        if (isNaN(stockValue) || stockValue < 0) {
          newErrors[`${skuId}_stock`] = 'Estoque inválido';
        }
      }
    }

    setSkuErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveSku = async (skuId, overrides = {}) => {
    const skuData = { ...skus.find((s) => s.id === skuId), ...overrides };
    if (!skuData.id) return;

    if (!validateSku(skuData, skuId)) {
      return;
    }

    setSavingSkuIds((prev) => new Set([...prev, skuId]));
    try {
      const attributesObj = {};
      const attrs = skuData.attributes;
      const entries = attrs && typeof attrs === 'object' && !Array.isArray(attrs)
        ? Object.entries(attrs).map(([key, value]) => ({ key: String(key), value: value != null ? String(value) : '' }))
        : [];
      entries.forEach((attr) => {
        if (attr.key && attr.key.trim() && attr.value != null && String(attr.value).trim()) {
          attributesObj[attr.key.trim()] = String(attr.value).trim();
        }
      });

      const isMTO = !!skuData.is_made_to_order;
      const priceVal = typeof skuData.price === 'string' ? parseFloat(parseCurrency(skuData.price)) : (skuData.price ?? 0);
      const skuPayload = {
        product_id: id,
        code: skuData.code || '',
        price: isNaN(priceVal) ? 0 : priceVal,
        price_type: skuData.price_type || 'FIXED',
        stock: isMTO ? 0 : parseInt(skuData.stock, 10),
        is_made_to_order: isMTO,
        manufacturing_days: isMTO ? (parseInt(skuData.manufacturing_days, 10) || 0) : 0,
        status: skuData.status,
        attributes: attributesObj,
      };

      await updateSKU(skuId, skuPayload);
      setSnackbar({
        open: true,
        message: 'SKU atualizado com sucesso',
        severity: 'success',
      });
      await loadData(false);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      setSavingSkuIds((prev) => {
        const next = new Set(prev);
        next.delete(skuId);
        return next;
      });
    }
  };

  const handleDeleteProductClick = () => {
    setConfirmDialog({
      open: true,
      type: 'product',
      payload: null,
    });
  };

  const handleDeleteProduct = async () => {
    setConfirmDialog({ open: false, type: null, payload: null });

    try {
      await deleteProduct(id);
      setSnackbar({
        open: true,
        message: translate('products.deleted'),
        severity: 'success',
      });
      setTimeout(() => {
        navigate('/products');
      }, 1000);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    }
  };

  const handleConfirmDelete = async () => {
    const { type, payload } = confirmDialog;
    setConfirmDialog({ open: false, type: null, payload: null });
    const effectiveProductId = productId || id;

    try {
      if (type === 'product') {
        await deleteProduct(id);
        setSnackbar({
          open: true,
          message: translate('products.deleted'),
          severity: 'success',
        });
        setTimeout(() => navigate('/products'), 1000);
      } else if (type === 'attribute' && payload && effectiveProductId) {
        await deleteProductAttribute(effectiveProductId, payload.attributeId, true);
        setSnackbar({
          open: true,
          message: 'Atributo excluído com sucesso',
          severity: 'success',
        });
        await loadData(false);
      } else if (type === 'option' && payload && effectiveProductId) {
        await deleteProductAttributeOption(effectiveProductId, payload.attributeId, payload.optionId, true);
        setSnackbar({
          open: true,
          message: 'Opção excluída com sucesso',
          severity: 'success',
        });
        await loadData(false);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    }
  };

  const handleConfirmCancel = () => {
    setConfirmDialog({ open: false, type: null, payload: null });
  };

  // Product Attributes Management
  const startNewAttribute = () => {
    setNewAttribute({
      name: '',
      is_customizable: false,
      is_variation: false,
      is_required: false,
      customizable_type: '',
      allow_other: false,
      display_order: productAttributes.length,
      options: [],
    });
  };

  // Drag and Drop handlers
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const optionSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEndAttributes = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = productAttributes.findIndex((attr) => attr.id === active.id);
    const newIndex = productAttributes.findIndex((attr) => attr.id === over.id);

    const newAttributes = arrayMove(productAttributes, oldIndex, newIndex);
    // Update display_order based on new position
    const updatedAttributes = newAttributes.map((attr, index) => ({
      ...attr,
      display_order: index,
    }));
    
    setProductAttributes(updatedAttributes);

    // Save order to backend
    try {
      for (let i = 0; i < updatedAttributes.length; i++) {
        const attr = updatedAttributes[i];
        const oldIndex = productAttributes.findIndex((a) => a.id === attr.id);
        if (oldIndex !== i) {
          await updateProductAttribute(productId, attr.id, {
            name: attr.name || attr.key,
            is_customizable: attr.is_customizable,
            is_variation: attr.is_variation,
            is_required: attr.is_required,
            customizable_type: attr.customizable_type || null,
            allow_other: attr.is_customizable && attr.customizable_type === 'select' ? (attr.allow_other || false) : false,
            display_order: i,
          });
        }
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar ordem dos atributos',
        severity: 'error',
      });
      await loadData(false);
    }
  };

  const handleDragEndOptions = async (event, attributeId) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const attribute = productAttributes.find((attr) => attr.id === attributeId);
    if (!attribute || !attribute.options) return;

    const oldIndex = attribute.options.findIndex((opt) => opt.id === active.id);
    const newIndex = attribute.options.findIndex((opt) => opt.id === over.id);

    const newOptions = arrayMove(attribute.options, oldIndex, newIndex);
    // Update display_order based on new position
    const updatedOptions = newOptions.map((opt, index) => ({
      ...opt,
      display_order: index,
    }));

    // Update local state immediately for better UX
    setProductAttributes((prev) =>
      prev.map((attr) =>
        attr.id === attributeId ? { ...attr, options: updatedOptions } : attr
      )
    );

  };

  const cancelNewAttribute = () => {
    setNewAttribute(null);
  };

  const handleAttributeChange = (attributeId, field, value) => {
    const applyMutualExclusion = (prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'is_customizable' && value) {
        next.is_variation = false;
      } else if (field === 'is_variation' && value) {
        next.is_customizable = false;
        next.customizable_type = '';
        next.allow_other = false;
      } else if (field === 'customizable_type' && value !== 'select') {
        next.allow_other = false;
      }
      return next;
    };
    if (attributeId === 'new') {
      setNewAttribute((prev) => applyMutualExclusion(prev));
    } else {
      setProductAttributes((prev) =>
        prev.map((attr) => (attr.id === attributeId ? applyMutualExclusion(attr) : attr))
      );
    }
  };

  const handleSaveAttribute = async (attributeId) => {
    if (!productId) return;

    let attributeData;
    if (attributeId === 'new') {
      attributeData = newAttribute;
    } else {
      attributeData = productAttributes.find((a) => a.id === attributeId);
    }

    if (!attributeData.name || !attributeData.name.trim()) {
      setSnackbar({
        open: true,
        message: translate('products.attribute_name') + ' é obrigatório',
        severity: 'error',
      });
      return;
    }
    if (attributeData.is_customizable && (!attributeData.customizable_type || !attributeData.customizable_type.trim())) {
      setSnackbar({
        open: true,
        message: translate('products.attribute_customizable_type') + ' é obrigatório quando o atributo é customizável',
        severity: 'error',
      });
      return;
    }
    if (attributeData.is_customizable && attributeData.customizable_type === 'select' && attributeId !== 'new') {
      const opts = attributeData.options || [];
      if (opts.length === 0) {
        setSnackbar({
          open: true,
          message: 'Tipo seleção exige ao menos uma opção',
          severity: 'error',
        });
        return;
      }
    }

    try {
      const payload = {
        name: attributeData.name.trim(),
        is_customizable: attributeData.is_customizable || false,
        is_variation: attributeData.is_variation || false,
        is_required: attributeData.is_required || false,
        customizable_type: attributeData.is_customizable && attributeData.customizable_type ? attributeData.customizable_type : null,
        allow_other: attributeData.is_customizable && attributeData.customizable_type === 'select' ? (attributeData.allow_other || false) : false,
        display_order: attributeId === 'new' ? productAttributes.length : (attributeData.display_order ?? productAttributes.length),
      };

      if (attributeId === 'new') {
        await createProductAttribute(productId, payload);
        setSnackbar({
          open: true,
          message: 'Atributo criado com sucesso',
          severity: 'success',
        });
        setNewAttribute(null);
      } else {
        await updateProductAttribute(productId, attributeId, payload);
        setSnackbar({
          open: true,
          message: 'Atributo atualizado com sucesso',
          severity: 'success',
        });
        setEditingAttributeId(null);
      }

      await loadData(false);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    }
  };

  const handleDeleteAttribute = async (attributeId) => {
    if (!productId) return;
    const attribute = productAttributes.find((a) => a.id === attributeId);
    try {
      const { affected_skus } = await getAffectedSKUsByAttribute(productId, attributeId);
      if (affected_skus && affected_skus.length > 0) {
        setConfirmDialog({
          open: true,
          type: 'attribute',
          payload: {
            attributeId,
            attributeName: attribute?.name || attribute?.key || 'Atributo',
            affectedSkus: affected_skus,
          },
        });
      } else {
        await deleteProductAttribute(productId, attributeId);
        setSnackbar({
          open: true,
          message: 'Atributo excluído com sucesso',
          severity: 'success',
        });
        await loadData(false);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    }
  };

  const handleAddOption = async (attributeId, value) => {
    if (!productId || !value || !value.trim()) return;
    try {
      const attribute = productAttributes.find((attr) => attr.id === attributeId);
      const currentOptionsCount = attribute?.options?.length || 0;
      await createProductAttributeOption(productId, attributeId, {
        value: value.trim(),
        display_order: currentOptionsCount,
      });
      await loadData(false);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    }
  };

  const handleDeleteOption = async (attributeId, optionId) => {
    const effectiveProductId = productId || id;
    if (!effectiveProductId) return;
    try {
      await deleteProductAttributeOption(effectiveProductId, attributeId, optionId);
      setSnackbar({
        open: true,
        message: 'Opção excluída com sucesso',
        severity: 'success',
      });
      await loadData(false);
    } catch (error) {
      if (error?.message?.toLowerCase().includes('skus')) {
        let affectedSkus = [];
        try {
          const response = await getAffectedSKUsByOption(effectiveProductId, attributeId, optionId);
          affectedSkus = response?.affected_skus ?? response?.data?.affected_skus ?? [];
        } catch (_) {}
        const attribute = productAttributes.find((a) => a.id === attributeId);
        const option = attribute?.options?.find((o) => o.id === optionId);
        setConfirmDialog({
          open: true,
          type: 'option',
          payload: {
            attributeId,
            optionId,
            attributeName: attribute?.name || attribute?.key || 'Atributo',
            optionValue: option?.value || 'Opção',
            affectedSkus: Array.isArray(affectedSkus) ? affectedSkus : [],
          },
        });
      } else {
        setSnackbar({
          open: true,
          message: error.message,
          severity: 'error',
        });
      }
    }
  };

  const getPriceDisplayValue = (sku) => {
    if (sku.price === '' || sku.price === null || sku.price === undefined) return '';
    if (typeof sku.price === 'number') {
      return applyCurrencyMask(Math.round(sku.price * 100).toString());
    }
    return sku.price;
  };

  const renderSkuRow = (sku) => {
    const skuId = sku.id;
    const isDefault = sku.is_default || false;
    const isSaving = savingSkuIds.has(skuId);

    return (
      <TableRow key={skuId}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {sku.code}
            {isDefault && (
              <Chip
                label="Padrão"
                size="small"
                color="primary"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Box sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {formatAttributes(sku.attributes)}
          </Box>
        </TableCell>
        <TableCell>
          <TextField
            size="small"
            value={getPriceDisplayValue(sku)}
            onChange={(e) => handleSkuChange(skuId, 'price', e.target.value)}
            onBlur={() => handleSaveSku(skuId)}
            error={!!skuErrors[`${skuId}_price`]}
            helperText={skuErrors[`${skuId}_price`]}
            disabled={isSaving}
            sx={{ minWidth: 100 }}
          />
        </TableCell>
        <TableCell>
          <Checkbox
            checked={(sku.status || 'ACTIVE') === 'ACTIVE'}
            onChange={(e) => handleSkuChange(skuId, 'status', e.target.checked ? 'ACTIVE' : 'INACTIVE', { autoSave: true })}
            disabled={isSaving}
          />
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Checkbox
              checked={!!sku.is_made_to_order}
              onChange={(e) => handleSkuChange(skuId, 'is_made_to_order', e.target.checked, { autoSave: true })}
              disabled={isSaving}
            />
            {sku.is_made_to_order ? (
              <TextField
                size="small"
                type="number"
                label={translate('products.manufacturing_days')}
                value={sku.manufacturing_days ?? ''}
                onChange={(e) => handleSkuChange(skuId, 'manufacturing_days', e.target.value)}
                onBlur={() => handleSaveSku(skuId)}
                error={!!skuErrors[`${skuId}_manufacturing_days`]}
                helperText={skuErrors[`${skuId}_manufacturing_days`]}
                disabled={isSaving}
                inputProps={{ min: 0 }}
                sx={{ minWidth: 140 }}
              />
            ) : (
              <TextField
                size="small"
                type="number"
                label={translate('products.stock')}
                value={sku.stock ?? ''}
                onChange={(e) => handleSkuChange(skuId, 'stock', e.target.value)}
                onBlur={() => handleSaveSku(skuId)}
                error={!!skuErrors[`${skuId}_stock`]}
                helperText={skuErrors[`${skuId}_stock`]}
                disabled={isSaving}
                inputProps={{ min: 0 }}
                sx={{ minWidth: 140 }}
              />
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  if (loadingData) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          {translate('common.loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          {isEdit ? translate('products.edit') : translate('products.create')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {isEdit && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteProductClick}
            >
              Excluir Produto
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? translate('common.loading') : translate('common.save')}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(isEdit ? `/products/${id}` : '/products')}
          >
            {translate('common.cancel')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
          <Box sx={{ flex: { xs: '1', sm: '0.5' } }}>
            <TextField
              fullWidth
              label={translate('products.name')}
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
          </Box>
          {isEdit && formData.code && (
            <Box sx={{ flex: { xs: '1', sm: '0.2' } }}>
              <TextField
                fullWidth
                label={translate('products.code')}
                value={formData.code}
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiInputBase-root': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
              />
            </Box>
          )}
          <Box sx={{ flex: { xs: '1', sm: '0.25' } }}>
            <TextField
              fullWidth
              select
              label={translate('products.item_type')}
              name="type"
              value={formData.type || 'PRODUCT'}
              onChange={handleChange}
            >
              <MenuItem value="PRODUCT">{translate('products.item_type_product')}</MenuItem>
              <MenuItem value="SERVICE">{translate('products.item_type_service')}</MenuItem>
            </TextField>
          </Box>
          <Box sx={{ flex: { xs: '1', sm: '0.25' } }}>
            <TextField
              fullWidth
              select
              label={translate('products.status')}
              name="status"
              value={formData.status}
              onChange={handleChange}
              error={!!errors.status}
              helperText={errors.status}
              required
            >
              <MenuItem value="ACTIVE">{translate('products.active')}</MenuItem>
              <MenuItem value="INACTIVE">{translate('products.inactive')}</MenuItem>
            </TextField>
          </Box>
        </Box>
        <Box>
          <TextField
            fullWidth
            label={translate('products.description')}
            name="description"
            value={formData.description}
            onChange={handleChange}
            error={!!errors.description}
            helperText={errors.description}
            multiline
            rows={4}
          />
        </Box>
      </Box>

      {isEdit && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" component="h2" gutterBottom>
            {translate('products.image_gallery')}
          </Typography>
          <MediaGallery
            metadataKey="product_image"
            metadataValue={productId || id}
            accept="image/*"
            onMediaChange={(medias) => {
              console.log('Galeria atualizada:', medias);
            }}
          />
        </Box>
      )}

      {isEdit && (
        <>
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5">
                Atributos do Produto
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={startNewAttribute}
                disabled={!!newAttribute}
              >
                Adicionar Atributo
              </Button>
            </Box>

            {newAttribute && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TextField
                        fullWidth
                        label={translate('products.attribute_name')}
                        value={newAttribute.name}
                        onChange={(e) => handleAttributeChange('new', 'name', e.target.value)}
                        placeholder="Ex: Cor, Tamanho"
                      />
                      <FieldHelpIcon title={translate('products.attribute_name_help')} />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newAttribute.is_variation}
                          onChange={(e) => handleAttributeChange('new', 'is_variation', e.target.checked)}
                          disabled={newAttribute.is_customizable}
                        />
                      }
                      label={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                          {translate('products.attribute_variation')}
                          <FieldHelpIcon title={translate('products.attribute_variation_help')} />
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newAttribute.is_customizable}
                          onChange={(e) => handleAttributeChange('new', 'is_customizable', e.target.checked)}
                          disabled={newAttribute.is_variation}
                        />
                      }
                      label={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                          {translate('products.attribute_customizable')}
                          <FieldHelpIcon title={translate('products.attribute_customizable_help')} />
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newAttribute.is_required}
                          onChange={(e) => handleAttributeChange('new', 'is_required', e.target.checked)}
                        />
                      }
                      label={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                          {translate('products.attribute_required')}
                          <FieldHelpIcon title={translate('products.attribute_required_help')} />
                        </Box>
                      }
                    />
                  </Grid>
                  {newAttribute.is_customizable && (
                    <Grid item xs={12} md={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label={translate('products.attribute_customizable_type')}
                          value={newAttribute.customizable_type || ''}
                          onChange={(e) => handleAttributeChange('new', 'customizable_type', e.target.value)}
                        >
                          <MenuItem value="text">Texto</MenuItem>
                          <MenuItem value="number">Número</MenuItem>
                          <MenuItem value="date">Data</MenuItem>
                          <MenuItem value="textarea">Texto longo</MenuItem>
                          <MenuItem value="select">Seleção</MenuItem>
                        </TextField>
                        <FieldHelpIcon title={translate('products.attribute_customizable_type_help')} />
                      </Box>
                    </Grid>
                  )}
                  {newAttribute.is_customizable && newAttribute.customizable_type === 'select' && (
                    <Grid item xs={12} md={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={newAttribute.allow_other || false}
                            onChange={(e) => handleAttributeChange('new', 'allow_other', e.target.checked)}
                          />
                        }
                        label={
                          <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                            {translate('products.attribute_allow_other')}
                            <FieldHelpIcon title={translate('products.attribute_allow_other_help')} />
                          </Box>
                        }
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} md={newAttribute.is_customizable && newAttribute.customizable_type === 'select' ? 1 : (newAttribute.is_customizable ? 1 : 5)}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleSaveAttribute('new')}
                      >
                        Salvar
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={cancelNewAttribute}
                      >
                        Cancelar
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            )}

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEndAttributes}
            >
              <SortableContext
                items={productAttributes.map(attr => attr.id)}
                strategy={verticalListSortingStrategy}
              >
                {productAttributes.map((attr) => (
                  <SortableAttributeItem
                    key={attr.id}
                    attribute={attr}
                    onDelete={handleDeleteAttribute}
                    onAddOption={handleAddOption}
                    editingOptionAttributeId={editingOptionAttributeId}
                    setEditingOptionAttributeId={setEditingOptionAttributeId}
                    handleDeleteOption={handleDeleteOption}
                    onDragEndOptions={handleDragEndOptions}
                    optionSensors={optionSensors}
                    isEditing={editingAttributeId === attr.id}
                    onEdit={() => setEditingAttributeId(attr.id)}
                    onSaveEdit={(aid) => handleSaveAttribute(aid)}
                    onCancelEdit={() => setEditingAttributeId(null)}
                    onAttributeChange={handleAttributeChange}
                  />
                ))}
              </SortableContext>
            </DndContext>

            {productAttributes.length === 0 && !newAttribute && (
              <Alert severity="info">
                Nenhum atributo definido. Adicione atributos para controlar as variações dos SKUs deste produto.
              </Alert>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
            <Typography variant="h5" component="h2">
              SKUs
            </Typography>
          </Box>

          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{translate('products.code')}</TableCell>
                  <TableCell>Atributos</TableCell>
                  <TableCell>Preço</TableCell>
                  <TableCell>Ativo</TableCell>
                  <TableCell>Sob demanda</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingData ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {translate('common.loading')}
                    </TableCell>
                  </TableRow>
                ) : skus.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Nenhum SKU encontrado. Adicione atributos obrigatórios com opções para gerar variações automaticamente.
                    </TableCell>
                  </TableRow>
                ) : (
                  skus.map((sku) => renderSkuRow(sku))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        title={
          confirmDialog.type === 'product'
            ? 'Confirmar exclusão de produto'
            : confirmDialog.type === 'attribute'
            ? `Excluir atributo "${confirmDialog.payload?.attributeName || ''}"`
            : confirmDialog.type === 'option'
            ? `Excluir opção "${confirmDialog.payload?.optionValue || ''}" do atributo "${confirmDialog.payload?.attributeName || ''}"`
            : 'Confirmar'
        }
        message={
          confirmDialog.type === 'product'
            ? 'Tem certeza que deseja excluir este produto? Esta ação não pode ser desfeita.'
            : (confirmDialog.type === 'attribute' || confirmDialog.type === 'option') && confirmDialog.payload?.affectedSkus?.length > 0
            ? `${confirmDialog.payload.affectedSkus.length} SKU(s) serão excluídos. Revise a lista abaixo antes de confirmar:`
            : 'Tem certeza que deseja excluir?'
        }
        detail={
          (confirmDialog.type === 'attribute' || confirmDialog.type === 'option') &&
          confirmDialog.payload?.affectedSkus?.length > 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, maxHeight: 280, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Atributos</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Preço</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Sob demanda</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {confirmDialog.payload.affectedSkus.map((affected) => {
                    const fullSku = skus.find((s) => s.id === affected.id);
                    const attrs = fullSku?.attributes;
                    const attrsStr = attrs && typeof attrs === 'object' && !Array.isArray(attrs)
                      ? Object.entries(attrs).map(([k, v]) => `${k}: ${v}`).join(', ')
                      : Array.isArray(attrs)
                        ? attrs.map((a) => `${a.key || a.attribute_key}: ${a.value || a.attribute_value}`).join(', ')
                        : '-';
                    const price = fullSku?.price != null ? formatCurrency(fullSku.price) : '-';
                    const isMTO = fullSku?.is_made_to_order ? 'Sim' : 'Não';
                    return (
                      <TableRow key={affected.id}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{affected.code}</TableCell>
                        <TableCell sx={{ fontSize: '0.85rem', maxWidth: 200 }}>{attrsStr}</TableCell>
                        <TableCell>{price}</TableCell>
                        <TableCell>{isMTO}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null
        }
        onConfirm={handleConfirmDelete}
        onCancel={handleConfirmCancel}
        confirmText="Excluir"
        cancelText="Cancelar"
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductFormPage;
